import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type { ApiResponse } from '@/types'

// ─── Domain Types ─────────────────────────────────────────────────────────────

export interface RewardCampaign {
  id: number
  account_id: number
  property_id: number | null
  name: string
  description: string | null
  status: 'draft' | 'active' | 'paused' | 'closed'
  earn_model: 'stamp' | 'points' | 'instant'
  earn_config: Record<string, unknown>
  funded_amount: number
  subsidy_amount: number
  consumed_amount: number
  pool_total: number
  pool_remaining: number
  funding_payment_id: number | null
  published_at: string | null
  starts_at: string | null
  ends_at: string | null
  disclosure: Record<string, unknown>
  partner_shops_count: number
  catalog_items_count: number
  created_at: string
  updated_at: string
}

export interface PartnerShopDevice {
  id: number
  device_label: string
  session_expires_at: string | null
  last_used_at: string | null
  revoked_at: string | null
  live: boolean
}

export interface PartnerShop {
  id: number
  account_id: number
  campaign_id: number
  name: string
  contact_name: string | null
  phone: string | null
  tax_id: string | null
  address: string | null
  geo: Record<string, unknown>
  status: string
  phone_verified_at: string | null
  has_live_session: boolean
  devices: PartnerShopDevice[]
  created_at: string
  updated_at: string
}

export interface RewardCatalogItem {
  id: number
  account_id: number
  campaign_id: number
  name: string
  reward_type: 'partner_voucher' | 'rent_credit' | 'flowrent_perk' | 'instant_discount'
  cost_points: number
  thb_value: number
  funding: string
  stock: number | null
  per_customer_limit: number | null
  active: boolean
  created_at: string
  updated_at: string
}

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const rewardCampaignKeys = {
  all: ['reward_campaigns'] as const,
  lists: () => [...rewardCampaignKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...rewardCampaignKeys.lists(), filters] as const,
  details: () => [...rewardCampaignKeys.all, 'detail'] as const,
  detail: (id: number) => [...rewardCampaignKeys.details(), id] as const,
}

export const partnerShopKeys = {
  all: ['partner_shops'] as const,
  lists: () => [...partnerShopKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...partnerShopKeys.lists(), filters] as const,
  details: () => [...partnerShopKeys.all, 'detail'] as const,
  detail: (id: number) => [...partnerShopKeys.details(), id] as const,
}

export const rewardCatalogItemKeys = {
  all: ['reward_catalog_items'] as const,
  lists: () => [...rewardCatalogItemKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...rewardCatalogItemKeys.lists(), filters] as const,
  details: () => [...rewardCatalogItemKeys.all, 'detail'] as const,
  detail: (id: number) => [...rewardCatalogItemKeys.details(), id] as const,
}

// ─── Filters ──────────────────────────────────────────────────────────────────

export interface RewardCampaignFilters {
  status?: string
  page?: number
}

export interface PartnerShopFilters {
  campaign_id?: number
  page?: number
}

export interface RewardCatalogItemFilters {
  campaign_id?: number
  active?: boolean
  page?: number
}

// ─── Campaign Hooks ───────────────────────────────────────────────────────────

export function useRewardCampaigns(page = 1, filters: RewardCampaignFilters = {}) {
  return useQuery({
    queryKey: rewardCampaignKeys.list({ page, ...filters }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (filters.status) params.status = filters.status
      const { data } = await api.get<ApiResponse<RewardCampaign[]>>('/api/reward_campaigns', { params })
      return data
    },
  })
}

export function useRewardCampaignDetail(id: number) {
  return useQuery({
    queryKey: rewardCampaignKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<RewardCampaign>>(`/api/reward_campaigns/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

interface CreateRewardCampaignInput {
  property_id?: number | null
  name: string
  description?: string | null
  earn_model: 'stamp' | 'points' | 'instant'
  status?: 'draft' | 'active' | 'paused' | 'closed'
  earn_config?: Record<string, unknown>
  disclosure?: Record<string, unknown>
}

export function useCreateRewardCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateRewardCampaignInput) => {
      const { data } = await api.post<ApiResponse<RewardCampaign>>('/api/reward_campaigns', {
        reward_campaign: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, rewardCampaignKeys.lists())
    },
  })
}

interface UpdateRewardCampaignInput extends Partial<CreateRewardCampaignInput> {
  id: number
}

export function useUpdateRewardCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateRewardCampaignInput) => {
      const { data } = await api.patch<ApiResponse<RewardCampaign>>(`/api/reward_campaigns/${id}`, {
        reward_campaign: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, rewardCampaignKeys.lists())
      invalidateDetailQueries(queryClient, rewardCampaignKeys.detail(data.id))
    },
  })
}

export function useDeleteRewardCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/reward_campaigns/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, rewardCampaignKeys.lists())
    },
  })
}

interface PublishRewardCampaignInput {
  id: number
  funded_amount: number
  subsidy_amount: number
  starts_at: string
  ends_at: string
}

export function usePublishRewardCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: PublishRewardCampaignInput) => {
      const { data } = await api.post<ApiResponse<RewardCampaign>>(
        `/api/reward_campaigns/${id}/publish`,
        body,
      )
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, rewardCampaignKeys.lists())
      invalidateDetailQueries(queryClient, rewardCampaignKeys.detail(data.id))
    },
  })
}

// ─── Partner Shop Hooks ───────────────────────────────────────────────────────

export function usePartnerShops(filters: PartnerShopFilters = {}) {
  return useQuery({
    queryKey: partnerShopKeys.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (filters.campaign_id) params.campaign_id = filters.campaign_id
      if (filters.page) params.page = filters.page
      const { data } = await api.get<ApiResponse<PartnerShop[]>>('/api/partner_shops', { params })
      return data
    },
    enabled: !!filters.campaign_id,
  })
}

interface CreatePartnerShopInput {
  campaign_id: number
  name: string
  contact_name?: string | null
  phone?: string | null
  tax_id?: string | null
  address?: string | null
  status?: string
  geo?: Record<string, unknown>
}

export function useCreatePartnerShop() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreatePartnerShopInput) => {
      const { data } = await api.post<ApiResponse<PartnerShop>>('/api/partner_shops', {
        partner_shop: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, partnerShopKeys.lists())
    },
  })
}

interface UpdatePartnerShopInput extends Partial<Omit<CreatePartnerShopInput, 'campaign_id'>> {
  id: number
}

export function useUpdatePartnerShop() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdatePartnerShopInput) => {
      const { data } = await api.patch<ApiResponse<PartnerShop>>(`/api/partner_shops/${id}`, {
        partner_shop: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, partnerShopKeys.lists())
      invalidateDetailQueries(queryClient, partnerShopKeys.detail(data.id))
    },
  })
}

export function useDeletePartnerShop() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/partner_shops/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, partnerShopKeys.lists())
    },
  })
}

export function useRevokePartnerShopSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<ApiResponse<PartnerShop>>(`/api/partner_shops/${id}/revoke`)
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, partnerShopKeys.lists())
      invalidateDetailQueries(queryClient, partnerShopKeys.detail(data.id))
    },
  })
}

export function useRotatePartnerShopToken() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, device_label }: { id: number; device_label?: string }) => {
      const { data } = await api.post<{ data: PartnerShop; session_token: string }>(
        `/api/partner_shops/${id}/rotate`,
        device_label ? { device_label } : {},
      )
      return data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, partnerShopKeys.lists())
      invalidateDetailQueries(queryClient, partnerShopKeys.detail(data.data.id))
    },
  })
}

// ─── Catalog Item Hooks ───────────────────────────────────────────────────────

export function useRewardCatalogItems(filters: RewardCatalogItemFilters = {}) {
  return useQuery({
    queryKey: rewardCatalogItemKeys.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (filters.campaign_id) params.campaign_id = filters.campaign_id
      if (filters.active !== undefined) params.active = filters.active
      if (filters.page) params.page = filters.page
      const { data } = await api.get<ApiResponse<RewardCatalogItem[]>>('/api/reward_catalog_items', { params })
      return data
    },
  })
}

interface CreateRewardCatalogItemInput {
  campaign_id: number
  name: string
  reward_type: 'partner_voucher' | 'rent_credit' | 'flowrent_perk' | 'instant_discount'
  cost_points: number
  thb_value: number
  funding: string
  stock?: number | null
  per_customer_limit?: number | null
  active?: boolean
}

export function useCreateRewardCatalogItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateRewardCatalogItemInput) => {
      const { data } = await api.post<ApiResponse<RewardCatalogItem>>('/api/reward_catalog_items', {
        reward_catalog_item: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, rewardCatalogItemKeys.lists())
    },
  })
}

interface UpdateRewardCatalogItemInput extends Partial<Omit<CreateRewardCatalogItemInput, 'campaign_id'>> {
  id: number
}

export function useUpdateRewardCatalogItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateRewardCatalogItemInput) => {
      const { data } = await api.patch<ApiResponse<RewardCatalogItem>>(`/api/reward_catalog_items/${id}`, {
        reward_catalog_item: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, rewardCatalogItemKeys.lists())
      invalidateDetailQueries(queryClient, rewardCatalogItemKeys.detail(data.id))
    },
  })
}

export function useDeleteRewardCatalogItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/reward_catalog_items/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, rewardCatalogItemKeys.lists())
    },
  })
}
